/**
 * Single source of truth for entity-kind display metadata (icon + label).
 * Consumed by every surface that renders an entity reference: HomePage cards,
 * CalendarLinkedNote, future search results, etc.
 *
 * If you add a new entity kind, add it here. Unknown kinds fall back to
 * `document`'s entry via `getEntityKindMeta`.
 *
 * Icon names are Material Symbols (the icon font we ship). See
 * https://fonts.google.com/icons for the catalogue.
 */

export interface EntityKindMeta {
  icon: string;
  label: string;
}

export const ENTITY_KIND_META: Record<string, EntityKindMeta> = {
  document: { icon: 'description', label: 'Document' },
  canvas: { icon: 'gesture', label: 'Canvas' },
  // `folder_open` (not `folder`) — the collections library, preview stack, and
  // trash all render the open-folder glyph; the map matches so the home-page
  // and calendar surfaces don't show a different (closed) folder.
  collection: { icon: 'folder_open', label: 'Collection' },
  // `auto_awesome` — the "Nula Chat" glyph the panel tab bar shows for a
  // conversation tab. Kept here as the single source of truth so the tab bar,
  // sidebar Recents, and any other conversation surface can't drift apart.
  conversation: { icon: 'auto_awesome', label: 'Conversation' },
  // Not an entities-supertable kind — `audio` is a document *surface* type.
  // It lives here so the surfaces panel, the New face, and anything that
  // later renders a recording all read one glyph. `graphic_eq` is a bare
  // waveform: the audio itself, as distinct from `speech_to_text`'s waveform
  // resolving into text, which is the transcript, not the capture.
  audio: { icon: 'graphic_eq', label: 'Audio' },
};

/**
 * Returns the meta entry for `kind`, falling back to the `document` entry
 * when the kind is unrecognized. Always returns a non-null object so callers
 * can safely `<Icon name={getEntityKindMeta(k).icon} />` without a guard.
 */
export function getEntityKindMeta(kind: string | null | undefined): EntityKindMeta {
  if (!kind) return ENTITY_KIND_META.document;
  return ENTITY_KIND_META[kind] ?? ENTITY_KIND_META.document;
}
